import { z } from 'zod';

import { normalizarEpc, normalizarPlaca } from '../texto';
import { telefonoSchema } from './usuarios';

/**
 * Esquemas del padrón. Los usan el API para validar y la web para el mismo
 * formulario, así que no pueden discrepar sobre qué es un dato válido.
 *
 * Los enums se declaran a mano por la misma razón que los roles: este paquete
 * lo carga el navegador y no puede importar el cliente de Prisma. Hay pruebas
 * del lado del API que verifican que no se separen del esquema.
 */

// ── Zonas ─────────────────────────────────────────────────────

export const crearZonaSchema = z.object({
  nombre: z.string().trim().min(1, 'La zona necesita nombre').max(80),
  orden: z.number().int().min(0).default(0),
});
export const actualizarZonaSchema = crearZonaSchema.partial();

export type CrearZona = z.infer<typeof crearZonaSchema>;
export type ActualizarZona = z.infer<typeof actualizarZonaSchema>;

// ── Unidades ──────────────────────────────────────────────────

export const TIPOS_UNIDAD = ['CASA', 'DEPARTAMENTO', 'LOCAL', 'LOTE'] as const;
export type TipoUnidad = (typeof TIPOS_UNIDAD)[number];

/** Una letra A-Z y hasta dos dígitos: A1, A14, B27, D5. */
export const CLAVE_UNIDAD_RE = /^[A-Z]\d{1,2}$/;

/**
 * El guardia teclea la clave con la reja abierta y el coche esperando: escribe
 * "a-14" o "a 14" según la costumbre de cada condominio. Si eso entrara tal
 * cual, la misma vivienda quedaría guardada de tres formas y ninguna búsqueda
 * las juntaría.
 */
export function normalizarClaveUnidad(clave: string): string {
  return clave.toUpperCase().replace(/[\s-]+/g, '');
}

/** Un solo texto para el API y la web: el guardia debe leer siempre lo mismo. */
export const MENSAJE_CLAVE_UNIDAD =
  'La clave de la vivienda es una letra (A-Z) y hasta dos dígitos: A1, A14, B27';

export const crearUnidadSchema = z.object({
  /**
   * Es lo que el guardia teclea y lo único que el kiosco muestra en la calle.
   *
   * El formato se exige al escribir, **no a lo ya guardado**. El padrón trae
   * claves anteriores ("A-101", "B-32000", "Casa 24") que son la casa real de
   * alguien; rechazarlas al leer dejaría esas viviendas fuera del kiosco y del
   * registro de accesos, que es exactamente lo contrario de lo que se pide.
   */
  clave: z
    .string()
    .trim()
    .min(1, 'La unidad necesita clave')
    // Con mensaje propio: el de zod para `max` sale en inglés y hablando de
    // caracteres, y es lo que vería el guardia pegado bajo el campo.
    .max(40, MENSAJE_CLAVE_UNIDAD)
    .transform(normalizarClaveUnidad)
    .refine((c) => CLAVE_UNIDAD_RE.test(c), MENSAJE_CLAVE_UNIDAD),
  tipo: z.enum(TIPOS_UNIDAD).default('CASA'),
  zonaId: z.string().cuid().nullish(),
  notas: z.string().trim().max(500).nullish(),

  /**
   * Domicilio de la vivienda. Todo opcional: en la mayoría de los
   * fraccionamientos la calle y el código postal son los del condominio, y
   * obligar a repetirlos en cada una de trescientas altas es cómo se consigue
   * que nadie capture ninguna. La app los hereda del condominio al dar de alta.
   */
  calle: z.string().trim().max(120).nullish(),
  numero: z.string().trim().max(20).nullish(),
  ciudad: z.string().trim().max(80).nullish(),
  municipio: z.string().trim().max(80).nullish(),
  pais: z.string().trim().max(60).nullish(),
  codigoPostal: z.string().trim().max(10).nullish(),

  /** Ubicación GPS, para el mapa y para orientar a un servicio de emergencia. */
  lat: z.coerce.number().min(-90).max(90).nullish(),
  lng: z.coerce.number().min(-180).max(180).nullish(),
});

// ── Teléfonos de la vivienda ──────────────────────────────────

/** Cuántos números marca la caseta antes de darse por vencida. */
export const MAX_TELEFONOS_UNIDAD = 3;

/**
 * Los números que la caseta marca **en cascada** cuando el guardia oprime
 * «Llamar»: primero el 1, y si no contestan, el siguiente.
 *
 * Son de la vivienda y no de una persona: quien contesta el fijo de la casa
 * puede no ser ninguno de los titulares, y eso está bien. El teléfono de
 * contacto del titular es otra cosa —a donde se le avisa— y vive en `Residente`.
 */
export const guardarTelefonosUnidadSchema = z.object({
  telefonos: z
    .array(
      z.object({
        numero: z
          .string()
          .trim()
          .min(7, 'Un teléfono necesita al menos 7 dígitos')
          .max(20)
          // Se guarda como lo escriben: el conmutador acepta espacios y guiones,
          // y normalizar aquí escondería un número mal tecleado detrás de un
          // formato bonito.
          .refine((n) => /\d{7,}/.test(n.replace(/\D/g, '')), 'Ese número no parece un teléfono'),
        etiqueta: z.string().trim().max(40).nullish(),
      }),
    )
    .max(MAX_TELEFONOS_UNIDAD, `La caseta marca hasta ${MAX_TELEFONOS_UNIDAD} números`),
});
export type GuardarTelefonosUnidad = z.infer<typeof guardarTelefonosUnidadSchema>;

/**
 * Parcial a propósito, y ahí está la compatibilidad con el padrón viejo: al
 * ser cada campo opcional, la regla de la clave solo corre cuando el PATCH
 * trae `clave`. Cambiarle la zona o las notas a la unidad "A-101" sigue
 * pasando; renombrarla obliga al formato nuevo, que es cuando sí toca migrarla.
 */
export const actualizarUnidadSchema = crearUnidadSchema.partial();

export type CrearUnidad = z.infer<typeof crearUnidadSchema>;
export type ActualizarUnidad = z.infer<typeof actualizarUnidadSchema>;

// ── Residentes ────────────────────────────────────────────────

export const TIPOS_RESIDENTE = ['TITULAR', 'HABITANTE'] as const;
export type TipoResidente = (typeof TIPOS_RESIDENTE)[number];

/** Hasta cuántos titulares admite una unidad. Lo cuida la app, no la base. */
export const MAX_TITULARES = 2;
/** Teléfonos de contacto que puede llevar un titular (avisos/WhatsApp). */
export const MAX_TELEFONOS_CONTACTO = 3;

/**
 * Dar de alta un residente crea también su `Usuario`: son la misma persona
 * vista desde el padrón y desde la autenticación, y separarlos en dos altas
 * solo produce residentes sin cuenta que nunca reciben un aviso.
 *
 * Nombre partido a la mexicana. El habitante lleva solo nombre y apellidos; el
 * titular, además, teléfono(s) de contacto. La app impone que un titular tenga
 * al menos uno y el tope de titulares por unidad.
 */
export const crearResidenteSchema = z
  .object({
    nombre: z.string().trim().min(1, 'El residente necesita nombre').max(80),
    apellidoPaterno: z.string().trim().min(1, 'Falta el apellido paterno').max(60),
    apellidoMaterno: z.string().trim().max(60).nullish(),
    email: z.string().trim().toLowerCase().email('Correo inválido').nullish(),
    telefono: telefonoSchema.nullish(),
    /** Teléfonos de contacto de la persona (avisos/WhatsApp), en orden. */
    telefonosContacto: z
      .array(z.string().trim().min(7, 'Teléfono demasiado corto').max(20))
      .max(MAX_TELEFONOS_CONTACTO)
      .default([]),
    unidadId: z.string().cuid(),
    tipo: z.enum(TIPOS_RESIDENTE).default('TITULAR'),
    /** Puede generar QR de invitación. El titular sí; el habitante si lo habilitan. */
    puedeAutorizar: z.boolean().default(true),
    /** Recibe el aviso de toda visita a su unidad. */
    recibeAvisos: z.boolean().default(true),
  })
  /**
   * **El titular necesita teléfono.**
   *
   * Es lo único con lo que va a poder recuperar su contraseña él solo: sin él,
   * olvidarla significa llamar a la administración y esperar a que alguien
   * esté. Y el titular es quien responde por la vivienda, genera los códigos y
   * recibe los avisos — la cuenta que de verdad se usa.
   *
   * **Al habitante no se le exige**, y eso no es una excepción perezosa: un
   * habitante puede ser un niño. Pedirle teléfono a un menor para darlo de alta
   * en el padrón de su propia casa sería absurdo. Si algún día se le da acceso
   * a la app, `darAcceso` ya se niega cuando no hay correo ni teléfono, que es
   * donde esa comprobación pertenece.
   */
  .refine((r) => r.tipo !== 'TITULAR' || Boolean(r.telefono), {
    message: 'El titular necesita teléfono: es con lo que recupera su contraseña',
    path: ['telefono'],
  });

/** En la edición cada campo es opcional; el titular/habitante se valida en el API. */
export const actualizarResidenteSchema = z.object({
  nombre: z.string().trim().min(1).max(80).optional(),
  apellidoPaterno: z.string().trim().min(1).max(60).optional(),
  apellidoMaterno: z.string().trim().max(60).nullish(),
  email: z.string().trim().toLowerCase().email().nullish(),
  telefono: telefonoSchema.nullish(),
  telefonosContacto: z
    .array(z.string().trim().min(7).max(20))
    .max(MAX_TELEFONOS_CONTACTO)
    .optional(),
  unidadId: z.string().cuid().optional(),
  tipo: z.enum(TIPOS_RESIDENTE).optional(),
  puedeAutorizar: z.boolean().optional(),
  recibeAvisos: z.boolean().optional(),
});

export type CrearResidente = z.infer<typeof crearResidenteSchema>;
export type ActualizarResidente = z.infer<typeof actualizarResidenteSchema>;

// ── Vehículos ─────────────────────────────────────────────────

export const crearVehiculoSchema = z.object({
  unidadId: z.string().cuid(),
  /**
   * Se normaliza al entrar, no al buscar. Si se guardara tal cual, "ABC-12-34"
   * y "abc1234" serían dos vehículos distintos y el guardia no encontraría el
   * que busca.
   */
  placa: z
    .string()
    .trim()
    .min(3, 'Placa demasiado corta')
    .max(15)
    .transform(normalizarPlaca)
    .refine((p) => p.length >= 3, 'La placa debe tener al menos 3 caracteres útiles'),
  marca: z.string().trim().max(40).nullish(),
  modelo: z.string().trim().max(40).nullish(),
  color: z.string().trim().max(30).nullish(),
});
export const actualizarVehiculoSchema = crearVehiculoSchema.partial();

export type CrearVehiculo = z.infer<typeof crearVehiculoSchema>;
export type ActualizarVehiculo = z.infer<typeof actualizarVehiculoSchema>;

// ── Tags ──────────────────────────────────────────────────────

export const TIPOS_TAG = ['UHF_VEHICULAR', 'PROXIMIDAD', 'CONTROL_REMOTO'] as const;
export type TipoTag = (typeof TIPOS_TAG)[number];

export const ESTADOS_TAG = ['ACTIVO', 'SUSPENDIDO', 'EXTRAVIADO', 'BAJA'] as const;
export type EstadoTag = (typeof ESTADOS_TAG)[number];

export const crearTagSchema = z
  .object({
    unidadId: z.string().cuid(),
    vehiculoId: z.string().cuid().nullish(),
    codigo: z.string().trim().min(4, 'Código demasiado corto').max(64),
    tipo: z.enum(TIPOS_TAG).default('UHF_VEHICULAR'),
    entregadoEn: z.coerce.date().nullish(),
  })
  // La normalización depende del tipo: a un EPC se le quita todo lo que no sea
  // hex, pero una tarjeta de proximidad puede traer letras fuera de A-F y
  // aplicarle lo mismo la destruiría. El EPC guardado tiene que coincidir
  // exactamente con lo que produzca `normalizarEpc` al leer del bus.
  .transform((t) => ({
    ...t,
    codigo:
      t.tipo === 'UHF_VEHICULAR'
        ? normalizarEpc(t.codigo)
        : t.codigo.toUpperCase().replace(/\s/g, ''),
  }))
  .refine((t) => t.codigo.length >= 4, {
    message: 'El código quedó vacío al normalizarlo. ¿Es un EPC hexadecimal válido?',
    path: ['codigo'],
  });

export const actualizarTagSchema = z.object({
  vehiculoId: z.string().cuid().nullish(),
  entregadoEn: z.coerce.date().nullish(),
});

export const cambiarEstadoTagSchema = z
  .object({
    estado: z.enum(ESTADOS_TAG),
    motivoEstado: z.string().trim().max(200).nullish(),
  })
  // Suspender o dar de baja un tag sin decir por qué convierte la bitácora en
  // un misterio el día que el residente reclame por qué no le abre la pluma.
  .refine((t) => t.estado === 'ACTIVO' || !!t.motivoEstado, {
    message: 'Indica el motivo: es lo que se le explica al residente después',
    path: ['motivoEstado'],
  });

export type CrearTag = z.infer<typeof crearTagSchema>;
export type ActualizarTag = z.infer<typeof actualizarTagSchema>;
export type CambiarEstadoTag = z.infer<typeof cambiarEstadoTagSchema>;

// ── El fraccionamiento ────────────────────────────────────────

/**
 * Los datos del condominio. Hasta ahora **solo se podían tocar entrando a la
 * base de datos**, incluido `retencionFotosDias`, que es el que tiene
 * consecuencia legal: dice cuántos días se conservan las fotos de las
 * identificaciones —con CURP, domicilio y firma— antes de borrarlas.
 *
 * El `slug` no está aquí y no es un olvido: viaja dentro de los tópicos MQTT de
 * todos los aparatos de campo y va firmado en cada código QR emitido.
 * Cambiarlo desconectaría las plumas y anularía las invitaciones vivas.
 */
/**
 * Que la zona exista de verdad.
 *
 * Se validaba como texto libre, y daba igual porque **nadie la leía**. Desde
 * que `vigenciaValida` decide con ella el día y la hora de una invitación
 * recurrente, un dedazo aquí deja a ese condominio sin poder validar ni una
 * sola: `Intl` lanza `RangeError` con una zona que no conoce.
 *
 * Se pregunta a `Intl` en vez de comparar contra una lista escrita a mano: la
 * lista se queda vieja, y esto acepta exactamente lo que el código va a saber
 * usar después.
 */
export function esZonaHorariaValida(zona: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zona });
    return true;
  } catch {
    return false;
  }
}

export const actualizarCondominioSchema = z.object({
  nombre: z.string().trim().min(1, 'El fraccionamiento necesita nombre').max(120).optional(),
  calle: z.string().trim().max(120).nullish(),
  ciudad: z.string().trim().max(80).nullish(),
  municipio: z.string().trim().max(80).nullish(),
  pais: z.string().trim().max(60).nullish(),
  codigoPostal: z.string().trim().max(10).nullish(),
  /** Con qué reloj se leen los horarios de las invitaciones y los reportes. */
  zonaHoraria: z
    .string()
    .trim()
    .max(60)
    .refine(esZonaHorariaValida, 'Esa zona horaria no existe.')
    .optional(),
  /**
   * Mínimo un día: poner cero borraría la foto antes de que el guardia pueda
   * mirarla. El tope de dos años es la otra guarda — «para siempre» no es una
   * política de retención, es no tener ninguna.
   */
  retencionFotosDias: z.coerce.number().int().min(1).max(730).optional(),
  /**
   * Si una visita sin código QR necesita tarjetón para poder entrar.
   *
   * Se puede apagar porque hay fraccionamientos que no reparten tarjetones, y
   * ahí la regla trabaría la reja sin dar nada a cambio. Apagarlo es una
   * decisión de quien administra, no un valor que convenga adivinar.
   */
  exigirTarjetonSinQr: z.boolean().optional(),
  /**
   * Los límites que el texto de la invitación le recuerda al visitante.
   *
   * Con tope de 120 por la misma razón que la retención tiene tope: no es un
   * campo donde tenga sentido cualquier número, y un dedazo acaba impreso en
   * el mensaje que reciben todos los visitantes del fraccionamiento.
   */
  limiteVelocidadCamellon: z.coerce.number().int().min(1).max(120).optional(),
  limiteVelocidadCalles: z.coerce.number().int().min(1).max(120).optional(),
});
export type ActualizarCondominio = z.infer<typeof actualizarCondominioSchema>;
